import { useEffect, useRef, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import { Loader2, MapPin } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Google Places autocomplete wrapped as a controlled input.
 *
 * Type to search; pick a suggestion; the parent receives a structured
 * `{ street, city, state, zip, lat, lng, formatted }` payload via
 * `onSelect`. The input itself stays controlled by the parent's `value`,
 * so manual editing still works (the address just isn't a Place anymore).
 *
 * Requires `VITE_GOOGLE_MAPS_BROWSER_KEY` and the Places API enabled on
 * that key. Falls back to a plain text input if the API can't load.
 */
export interface AutocompletedAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  lat: number;
  lng: number;
  formatted: string;
}

const loaderSingleton = (() => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY as string | undefined;
  if (!apiKey) return null;
  return new Loader({
    apiKey,
    version: "weekly",
    libraries: ["places"],
  });
})();

// Global handler Google calls when the API key auth fails (referer denied,
// API not enabled, billing missing, etc). Subscribers below get flipped to
// the "error" status so the UI shows a useful message instead of Google's
// alert modal.
type AuthFailureSubscriber = () => void;
const authFailureSubscribers = new Set<AuthFailureSubscriber>();
if (typeof window !== "undefined") {
  (window as unknown as { gm_authFailure?: () => void }).gm_authFailure = () => {
    authFailureSubscribers.forEach((cb) => {
      try { cb(); } catch { /* ignore */ }
    });
  };
}

function buildAddress(place: google.maps.places.PlaceResult): AutocompletedAddress | null {
  if (!place.geometry?.location) return null;
  const get = (type: string, useShort = false) => {
    const c = place.address_components?.find((x) => x.types.includes(type));
    return (useShort ? c?.short_name : c?.long_name) ?? "";
  };
  const streetNumber = get("street_number");
  const route = get("route");
  const street = [streetNumber, route].filter(Boolean).join(" ");
  const city = get("locality") || get("sublocality") || get("administrative_area_level_2");
  const state = get("administrative_area_level_1", true);
  const zip = get("postal_code");
  return {
    street,
    city,
    state: state.toUpperCase(),
    zip,
    lat: place.geometry.location.lat(),
    lng: place.geometry.location.lng(),
    formatted: place.formatted_address ?? "",
  };
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing an address…",
  required,
  className,
  inputClassName,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (addr: AutocompletedAddress) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  inputClassName?: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error" | "disabled">(
    loaderSingleton ? "loading" : "disabled",
  );

  useEffect(() => {
    if (!loaderSingleton || !inputRef.current) return;
    let cancelled = false;

    const onAuthFailure = () => {
      if (!cancelled) setStatus("error");
    };
    authFailureSubscribers.add(onAuthFailure);

    loaderSingleton
      .importLibrary("places")
      .then((places) => {
        if (cancelled || !inputRef.current) return;
        const ac = new places.Autocomplete(inputRef.current, {
          fields: ["address_components", "geometry", "formatted_address"],
          types: ["address"],
          componentRestrictions: { country: ["us"] },
        });
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          const parsed = buildAddress(place);
          if (!parsed) return;
          onChange(parsed.formatted || parsed.street);
          onSelect(parsed);
        });
        acRef.current = ac;
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("error");
      });
    return () => {
      cancelled = true;
      authFailureSubscribers.delete(onAuthFailure);
    };
    // Loader + handlers are stable; onChange/onSelect are intentionally
    // captured at mount via closure — re-initializing the widget every
    // keystroke would clobber the user's typed query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cn("relative", className)}>
      <MapPin className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "w-full rounded-md border-slate-300 pl-8 pr-8 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500",
          inputClassName,
        )}
        autoComplete="off"
      />
      {status === "loading" && (
        <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-slate-400" />
      )}
      {status === "error" && (
        <p className="mt-1 text-[11px] text-amber-700">
          Address suggestions are offline. Check that <code>places-backend.googleapis.com</code> is allowed on your Google Maps browser key and the current URL is in its referrer list. You can still type the address manually.
        </p>
      )}
      {status === "disabled" && (
        <p className="mt-1 text-[11px] text-slate-500">
          Set <code>VITE_GOOGLE_MAPS_BROWSER_KEY</code> to enable address suggestions.
        </p>
      )}
    </div>
  );
}
